import * as THREE from 'three';
import { Line2 } from 'three/addons/lines/Line2.js';
import { LineGeometry } from 'three/addons/lines/LineGeometry.js';
import { LineMaterial } from 'three/addons/lines/LineMaterial.js';
import { GLOBE_RADIUS } from '../core/globe';
import { greatCirclePath } from './great-circle';
import { isMobileDevice } from '../textures';
import type { LatLon } from '../geo/types';

/**
 * Animated great-circle arcs between two lat/lon points on the globe.
 * Used by city-distance game modes to "draw" from the user's guess to the
 * correct answer, by reveal screens to connect history dots, etc.
 *
 * Per arc:
 *   - Pre-computes 64 vertices along the great circle (with a radial bow
 *     so it sits visibly above the globe surface).
 *   - Builds one LineGeometry + one Line2 (shader-based wide line — real
 *     pixel thickness, unlike `THREE.Line` whose `linewidth` is ignored
 *     on every WebGL platform except some Chrome/Linux configs).
 *   - Each engine frame, advances `instanceCount` from 0 → segment count
 *     based on `(now - startTime) / durationMs`. Cheap — no geometry
 *     rebuild per frame, just one integer write.
 *   - On completion, fires onComplete and (by default) keeps the line
 *     visible so the static finished arc reads as a "path drawn" result.
 *     Caller can clear via cancel() or arcs.clear().
 *
 * Multiple arcs can run simultaneously — game modes that show several
 * connections at once (e.g. multi-round result screen) can fire-and-forget.
 *
 * Resolution gotcha: `LineMaterial` needs the canvas resolution in pixels
 * to translate `linewidth` (px) into clip space. Engine calls
 * `setResolution(w, h)` from its resize handler so material stays in sync.
 */

export interface ArcOptions {
  from: LatLon;
  to: LatLon;
  /** Total animation time in milliseconds. */
  durationMs: number;
  /**
   * Stroke color. Default `0xd4a24c` (design-system accent — same gold as
   * country borders so arcs feel like part of the surface).
   */
  color?: THREE.ColorRepresentation;
  /** 0–1 stroke opacity. Default 0.95. */
  opacity?: number;
  /** Stroke thickness in CSS pixels. Default device-aware (3 desktop, 4.5 touch). */
  linewidth?: number;
  /** Number of vertices in the path. Default 64; bump for very long arcs. */
  segments?: number;
  /**
   * Per-frame progress callback (t goes 0 → 1). Game modes wire this to
   * audio so ticks fire on a cadence aligned with the arc's progress.
   */
  onProgress?: (t: number) => void;
  /** Fired exactly once when the arc finishes (or never if cancelled first). */
  onComplete?: () => void;
  /**
   * Remove the arc geometry when the animation finishes. Default false —
   * the static finished arc usually stays on screen as part of the result
   * (cleared later via clear() / dispose()).
   */
  removeOnComplete?: boolean;
}

export interface ArcHandle {
  /** Stop the animation (and remove the arc) immediately. Idempotent. */
  cancel(): void;
  /** Resolves when the arc completes. Rejects with `'cancelled'` if cancelled. */
  done: Promise<void>;
}

interface ActiveArc {
  startMs: number;
  durationMs: number;
  /** Total drawable segments in the geometry — one less than vertex count. */
  segmentCount: number;
  line: Line2;
  geometry: LineGeometry;
  material: LineMaterial;
  onProgress?: (t: number) => void;
  onComplete?: () => void;
  removeOnComplete: boolean;
  resolve: () => void;
  reject: (reason: string) => void;
  cancelled: boolean;
  completed: boolean;
}

const DEFAULT_COLOR = 0xd4a24c;
/** Touch devices get a beefier line — finger occlusion + lower-DPI eyes. */
const DEFAULT_LINEWIDTH_DESKTOP = 3;
const DEFAULT_LINEWIDTH_TOUCH = 4.5;

/**
 * Arc system. One per MapEngine; exposed as `engine.arcs`. Drive via
 * `engine.arcs.draw({...})`, advance via `engine.arcs.tick(now)` from the
 * render loop (engine wires this automatically).
 */
export class ArcManager {
  private readonly root: THREE.Group;
  private readonly active = new Set<ActiveArc>();
  private readonly defaultLinewidth: number;
  /** Latest canvas resolution in CSS pixels — kept in sync via setResolution(). */
  private readonly resolution = new THREE.Vector2(1, 1);
  /** Materials of every arc currently on the globe — bulk-updated on resize. */
  private readonly liveMaterials = new Set<LineMaterial>();

  constructor(parent: THREE.Group) {
    this.root = new THREE.Group();
    this.root.name = 'arcs';
    // Sit above borders (renderOrder 2) but below markers (groupOrder 3)
    // so an arc connecting two markers passes UNDER the markers — endpoint
    // dots stay visually on top, no occlusion. Same groupOrder = 0 issue
    // as fills, but renderOrder 2 still sorts correctly within that group.
    parent.add(this.root);
    this.defaultLinewidth = isMobileDevice()
      ? DEFAULT_LINEWIDTH_TOUCH
      : DEFAULT_LINEWIDTH_DESKTOP;
  }

  /**
   * Update every live + future arc's resolution uniform. Engine calls this
   * from its resize handler so wide-line shading stays correct after window
   * resizes / canvas reattaches.
   */
  setResolution(width: number, height: number): void {
    this.resolution.set(Math.max(1, width), Math.max(1, height));
    for (const mat of this.liveMaterials) {
      mat.resolution.copy(this.resolution);
    }
  }

  /**
   * Start drawing a new arc. Returns immediately; the animation advances
   * on each `tick()` from the engine's render loop. Call cancel() on the
   * handle (or `clear()` on the manager) to interrupt.
   */
  draw(options: ArcOptions): ArcHandle {
    const points = greatCirclePath(options.from, options.to, {
      segments: options.segments,
      surfaceRadius: GLOBE_RADIUS,
    });

    const flatPositions = new Array<number>(points.length * 3);
    for (let i = 0; i < points.length; i++) {
      flatPositions[i * 3] = points[i].x;
      flatPositions[i * 3 + 1] = points[i].y;
      flatPositions[i * 3 + 2] = points[i].z;
    }

    const geometry = new LineGeometry();
    geometry.setPositions(flatPositions);
    // Start hidden — first tick() advances instanceCount once the
    // animation has progressed. Without this, frame 1 would render the
    // entire arc before the animation kicks in.
    geometry.instanceCount = 0;

    const material = new LineMaterial({
      color: new THREE.Color(options.color ?? DEFAULT_COLOR).getHex(),
      opacity: options.opacity ?? 0.95,
      transparent: true,
      depthWrite: false,
      linewidth: options.linewidth ?? this.defaultLinewidth,
      worldUnits: false,
      resolution: this.resolution.clone(),
    });

    const line = new Line2(geometry, material);
    line.name = 'arc';
    line.renderOrder = 2;
    // Line2's instance buffer doesn't auto-cull correctly on every GPU —
    // turning frustum culling off avoids the line vanishing at sharp angles.
    line.frustumCulled = false;
    this.root.add(line);
    this.liveMaterials.add(material);

    let resolve!: () => void;
    let reject!: (reason: string) => void;
    const done = new Promise<void>((res, rej) => {
      resolve = res;
      reject = rej;
    });

    const arc: ActiveArc = {
      startMs: performance.now(),
      durationMs: options.durationMs,
      segmentCount: Math.max(0, points.length - 1),
      line,
      geometry,
      material,
      onProgress: options.onProgress,
      onComplete: options.onComplete,
      removeOnComplete: options.removeOnComplete ?? false,
      resolve,
      reject,
      cancelled: false,
      completed: false,
    };
    this.active.add(arc);

    return {
      cancel: () => this.cancelArc(arc),
      done,
    };
  }

  /** Called once per frame from the engine render loop. */
  tick(nowMs: number): void {
    if (this.active.size === 0) return;
    for (const arc of this.active) {
      if (arc.cancelled || arc.completed) continue;
      const elapsed = nowMs - arc.startMs;
      const t = Math.min(1, elapsed / arc.durationMs);
      // instanceCount counts segments rendered (each segment = one start/
      // end pair instance). 0 = nothing visible; segmentCount = full arc.
      const visibleSegments = Math.max(
        0,
        Math.min(arc.segmentCount, Math.floor(t * arc.segmentCount)),
      );
      arc.geometry.instanceCount = visibleSegments;
      arc.onProgress?.(t);
      if (t >= 1) {
        arc.completed = true;
        // Snap to full segment count so the final frame doesn't lose
        // the trailing instance due to floor() above.
        arc.geometry.instanceCount = arc.segmentCount;
        arc.onComplete?.();
        if (arc.removeOnComplete) this.removeArc(arc);
        else this.active.delete(arc); // leave the line visible, stop ticking
        arc.resolve();
      }
    }
  }

  /** Remove every arc currently on the globe — animating or completed. */
  clear(): void {
    for (const arc of this.active) {
      arc.cancelled = true;
      this.removeArc(arc);
    }
    // Also remove any completed-but-still-visible arcs.
    for (const child of [...this.root.children]) {
      this.disposeChild(child);
    }
  }

  /** Free GPU resources. Engine calls on dispose. */
  dispose(): void {
    this.clear();
    this.root.removeFromParent();
  }

  // -- internals --

  private cancelArc(arc: ActiveArc): void {
    if (arc.cancelled || arc.completed) return;
    arc.cancelled = true;
    this.removeArc(arc);
    arc.reject('cancelled');
  }

  private removeArc(arc: ActiveArc): void {
    this.active.delete(arc);
    this.disposeChild(arc.line);
  }

  private disposeChild(child: THREE.Object3D): void {
    child.removeFromParent();
    const asLine = child as Line2;
    if (asLine.geometry) asLine.geometry.dispose();
    const material = asLine.material;
    if (material) {
      if (Array.isArray(material)) {
        for (const m of material) {
          this.liveMaterials.delete(m as LineMaterial);
          m.dispose();
        }
      } else {
        this.liveMaterials.delete(material as LineMaterial);
        material.dispose();
      }
    }
  }
}
